import { useEffect, useRef, useCallback } from 'react';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Cloudflare Turnstile widget.
 * Lazy-loads the Turnstile script and renders the challenge widget.
 *
 * Props:
 *   siteKey  — Cloudflare Turnstile site key (string)
 *   onVerify — callback(token) on successful verification
 *   onError  — optional callback(error) on failure
 */
export default function TurnstileWidget({ siteKey, onVerify, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const handleVerify = useCallback(
    (token) => { if (onVerify) onVerify(token); },
    [onVerify],
  );

  const handleError = useCallback(
    (err) => { if (onError) onError(err); },
    [onError],
  );

  useEffect(() => {
    if (!siteKey) return;

    const render = () => {
      if (!containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current != null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: handleVerify,
        'error-callback': handleError,
      });
    };

    if (window.turnstile) {
      render();
      return;
    }

    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', render);
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', render);
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, handleVerify, handleError]);

  return <div ref={containerRef} className="cf-turnstile" />;
}
